/**
 * The ONE in-flight primitive (R3). While an async surface is loading, it renders
 * these token-tinted placeholders instead of a zero-count / blank surface, so the
 * layout doesn't jump when the real data lands and an agent (or a person) can tell
 * "still loading" from "nothing here yet". The shimmer + reduced-motion fallback live
 * in the `.skeleton*` rules in index.html; the async container carries `aria-busy`.
 *
 * A skeleton is NEVER an empty-state — "noch nichts hier" copy stays `.surface-empty`.
 *
 * SHAPE RULE: a list skeleton wears the REAL row's own class and reproduces its child
 * structure, filling the slots with shimmer bars. It therefore inherits the real footprint
 * — hairline vs box, radius, padding, grid columns — and CANNOT drift from it. The earlier
 * one-size `.skeleton-card` was a 13px-radius shadowed box standing in for BOTH the
 * `.opportunity-card` (a zero-radius hairline row) and the `.application-card` (a
 * `--radius-md` box with a 4px accent), so the search list visibly de-boxed the moment data
 * arrived. Do not reintroduce a generic list-card placeholder.
 */

import { html, type TemplateResult } from "@platform/web-kit/render"

/**
 * N placeholder search results, shaped as the pipeline really is: an `.opp-group` bounded
 * item (the Genre-B card edge) opening with the company band, then one `.opportunity-card`
 * posting row — the two-column grid of `.opp-main` and the right-hand `.opp-rail` readout.
 * It wears the real classes, so it cannot drift from the loaded footprint the way a generic
 * placeholder box did.
 */
export function skeletonOpportunityRows(n: number): TemplateResult {
  return html`${Array.from(
    { length: n },
    () => html`
      <li class="opp-group card-item" aria-hidden="true">
        <span class="opp-company">
          <span class="opp-company-id">
            <span class="skeleton skeleton-line is-md"></span>
          </span>
        </span>
        <span class="opportunity-card">
          <span class="opp-main">
            <span class="skeleton skeleton-heading"></span>
            <span class="skeleton skeleton-line is-lg"></span>
          </span>
          <span class="opp-rail">
            <span class="skeleton skeleton-line is-sm"></span>
          </span>
        </span>
      </li>
    `,
  )}`
}

/**
 * N placeholder applications, shaped as `.application-card` really is: a bordered column
 * card opening with a status chip row rather than a heading.
 */
export function skeletonApplicationCards(n: number): TemplateResult {
  return html`${Array.from(
    { length: n },
    () => html`
      <li class="application-card" aria-hidden="true">
        <span class="skeleton skeleton-chip"></span>
        <span class="skeleton skeleton-line is-lg"></span>
        <span class="skeleton skeleton-line is-md"></span>
      </li>
    `,
  )}`
}

/** N placeholder run-rail / run-ledger rows (a single shimmering bar each). */
export function skeletonRows(n: number): TemplateResult {
  return html`${Array.from(
    { length: n },
    () => html`<div class="skeleton skeleton-row" aria-hidden="true"></div>`,
  )}`
}

/** The balance-figure placeholder — one large shimmering block. */
export function skeletonBalance(): TemplateResult {
  return html`<span class="skeleton skeleton-balance" aria-hidden="true"></span>`
}

/** A block of `n` shimmering text lines (last one short) for prose regions. */
export function skeletonText(n: number): TemplateResult {
  return html`${Array.from(
    { length: n },
    (_, i) => html`<span class="skeleton skeleton-line ${i === n - 1 ? "is-md" : "is-lg"}" aria-hidden="true"></span>`,
  )}`
}
